// Typed Firestore collection helpers + CRUD wrappers for Compass.

import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type CollectionReference,
  type DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { allocateRefs } from './refs';
import { ACTIONS_CHANGED_EVENT } from './actions';
import { resolveActiveTenantId, tenantWhere } from './tenant';
import {
  type Action,
  type ActionActivityEntry,
  type ActionLock,
  type AppSettings,
  type ChangeEntry,
  type Communication,
  type Contact,
  type DataImportAdminSettings,
  type DataImportAttempt,
  type EventTemplate,
  type Goal,
  type Initiative,
  type InitiativeMessage,
  type ISODateString,
  type MembershipSnapshot,
  type NoteEntry,
  type QualificationArea,
  type QualificationAssignment,
  type StrategicPriority,
  type Task,
  type Unit,
  type UnitAdvancementSnapshot,
  type UnitConnection,
  type UnitImport,
  type UnitKey3Snapshot,
  type UnitLeader,
  type UnitLeaderSnapshot,
  type UnitRecharterSnapshot,
  type UnitReportType,
  type UnitSyt,
  type UnitSytSnapshot,
  type UserProfile,
} from '../types';

function col<T = DocumentData>(name: string): CollectionReference<T> {
  return collection(db, name) as CollectionReference<T>;
}

export const collections = {
  actions: () => col<Omit<Action, 'id'>>('actions'),
  actionLocks: () => col<Omit<ActionLock, 'id'>>('actionLocks'),
  strategicPriorities: () => col<Omit<StrategicPriority, 'id'>>('strategicPriorities'),
  goals: () => col<Omit<Goal, 'id'>>('goals'),
  tasks: () => col<Omit<Task, 'id'>>('tasks'),
  contacts: () => col<Omit<Contact, 'id'>>('contacts'),
  users: () => col<Omit<UserProfile, 'id'>>('users'),
  eventTemplates: () => col<Omit<EventTemplate, 'id'>>('eventTemplates'),
  initiatives: () => col<Omit<Initiative, 'id'>>('initiatives'),
  units: () => col<Omit<Unit, 'id'>>('units'),
  unitAdvancementSnapshots: () =>
    col<Omit<UnitAdvancementSnapshot, 'id'>>('unitAdvancementSnapshots'),
  unitConnections: () => col<Omit<UnitConnection, 'id'>>('unitConnections'),
  unitImports: () => col<Omit<UnitImport, 'id'>>('unitImports'),
  unitLeaders: () => col<Omit<UnitLeader, 'id'>>('unitLeaders'),
  unitLeaderSnapshots: () => col<Omit<UnitLeaderSnapshot, 'id'>>('unitLeaderSnapshots'),
  unitSyt: () => col<Omit<UnitSyt, 'id'>>('unitSyt'),
  unitSytSnapshots: () => col<Omit<UnitSytSnapshot, 'id'>>('unitSytSnapshots'),
  unitRecharterSnapshots: () => col<Omit<UnitRecharterSnapshot, 'id'>>('unitRecharterSnapshots'),
  unitKey3Snapshots: () => col<Omit<UnitKey3Snapshot, 'id'>>('unitKey3Snapshots'),
  communications: () => col<Omit<Communication, 'id'>>('communications'),
  initiativeMessages: () => col<Omit<InitiativeMessage, 'id'>>('initiativeMessages'),
  membershipSnapshots: () => col<Omit<MembershipSnapshot, 'id'>>('membershipSnapshots'),
  qualificationAreas: () => col<Omit<QualificationArea, 'id'>>('qualificationAreas'),
  qualificationAssignments: () =>
    col<Omit<QualificationAssignment, 'id'>>('qualificationAssignments'),
};

// Settings cutover (Phase 1, next stage after Stage B): the app's
// authoritative configuration lives at tenants/{tenantId}/settings/app, not
// the legacy top-level settings/app singleton — see TenantSettings in
// ../types for why. resolveActiveTenantId() (src/lib/tenant.ts) is the
// caller's own trusted, membership-derived tenant (Phase 5A/5B) — never a
// client-chosen value.
export const settingsRef = () => doc(db, 'tenants', resolveActiveTenantId(), 'settings', 'app');

// Data Imports — Import Management config + last-attempt tracking. A
// deliberately SEPARATE doc/collection from settingsRef() above, NOT a
// sibling under tenants/{tenantId}/settings/{docId} — that path's
// firestore.rules match is `allow read: if hasProfile()`, i.e. readable by
// ANY signed-in tenant member, which is correct for ordinary app config
// but wrong for responsible-person identities and reminder configuration
// (must stay admin-only in both directions — see DataImportAdminSettings'
// own doc comment in ../types). dataImportAdminRef() therefore points at
// its own collection with its own strictly-admin-only firestore.rules
// block instead of reusing the generic settings pattern.
export const dataImportAdminRef = () =>
  doc(db, 'tenants', resolveActiveTenantId(), 'dataImportAdmin', 'config');

// ---- writes ---------------------------------------------------------------

// `tenantId` is stamped by these functions themselves (like createdAt/ref),
// never supplied by a caller, from resolveActiveTenantId()'s own trusted
// resolution (see src/lib/tenant.ts) — a caller can influence WHICH tenant
// it resolves to only by actually being a member of it.
type New<T> = Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'tenantId'>;

export async function createPriority(
  data: New<StrategicPriority>,
): Promise<string> {
  const ref = await addDoc(collections.strategicPriorities(), {
    ...data,
    tenantId: resolveActiveTenantId(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as never);
  return ref.id;
}

/** Creates the Action with its "created" activity entry already seeded —
 *  an Action is never actually empty. `activity`/`completedAt` are set
 *  here, not by the caller. */
export async function createAction(
  data: {
    title: string;
    description?: string;
    priority: Action['priority'];
    districtId: string;
    /** The owner is a Person (Phase 2.3). */
    ownerPersonId: string;
    subcommitteeId?: string | null;
    dueDate?: string | null;
    relatedUnitId?: string | null;
    relatedPersonId?: string | null;
    relatedGoalId?: string | null;
    relatedInitiativeId?: string | null;
  },
): Promise<string> {
  const result = await httpsCallable<
    {
      title: string;
      description?: string;
      priority: Action['priority'];
      districtId: string;
      ownerPersonId: string;
      subcommitteeId?: string | null;
      dueDate?: string | null;
      relatedUnitId?: string | null;
      relatedPersonId?: string | null;
      relatedGoalId?: string | null;
      relatedInitiativeId?: string | null;
    },
    { actionId: string; ref: string }
  >(functions, 'createManualActionV2')({
    title: data.title,
    description: data.description ?? '',
    priority: data.priority,
    districtId: data.districtId,
    ownerPersonId: data.ownerPersonId,
    subcommitteeId: data.subcommitteeId ?? null,
    dueDate: data.dueDate ?? null,
    relatedUnitId: data.relatedUnitId ?? null,
    relatedPersonId: data.relatedPersonId ?? null,
    relatedGoalId: data.relatedGoalId ?? null,
    relatedInitiativeId: data.relatedInitiativeId ?? null,
  });
  window.dispatchEvent(new Event(ACTIONS_CHANGED_EVENT));
  return result.data.actionId;
}

/** Reference implementation for Phase 2's system-generated Actions —
 *  concurrency-safe find-or-create by a deterministic `sourceKey`, so a
 *  retried import or two overlapping automation runs for the SAME
 *  real-world condition can never create two Actions. NOT called by
 *  anything yet — Phase 1 has no automation. Kept here (not lib/actions.ts)
 *  because it's a real Firestore write, matching this file's convention.
 *
 *  Why a query-then-create can't be trusted: `getDocs(where('sourceKey',
 *  '==', key))` followed by a conditional `addDoc()` is two separate,
 *  non-atomic round trips — two callers can both run the query, both see
 *  "nothing yet," and both create an Action for the same condition. A
 *  Firestore transaction over a single shared document closes that gap:
 *  Firestore guarantees only one of two conflicting transactions on the
 *  same doc commits; the other is automatically retried by the SDK and,
 *  on retry, observes the write the first one just made.
 *
 *  Mechanism: `actionLocks/{sourceKey}` is that shared document. Two
 *  concurrent calls for the same key both start a transaction that reads
 *  the lock doc; Firestore serializes them. Whichever commits first sees
 *  no lock, creates the Action, and creates the lock pointing at it in the
 *  SAME transaction. The other is forced to retry, now sees the lock the
 *  first one committed, and returns its actionId instead of creating a
 *  second Action — `created: false` tells the caller "someone already has
 *  this condition open, decide what (if anything) to update on it"
 *  (e.g. append an activity note) rather than creating a duplicate.
 *
 *  Freeing a key: call releaseActionLock() when the Action a lock points
 *  at resolves (completed/cancelled), so a later recurrence of the same
 *  condition can open a fresh Action rather than being stuck referencing
 *  an old, resolved one forever.
 *
 *  Deliberately doesn't set `ref` (the human-readable ACT-014 label):
 *  allocateRefs() is its own separate transaction (over counters/refs) and
 *  can't safely nest inside this one, and `buildAction()` may run more
 *  than once if this transaction retries under contention — allocating a
 *  ref per attempt would burn numbers for attempts that never become the
 *  real Action. Phase 2's caller should allocate one in a small follow-up
 *  write after a `created: true` result, when a ref is actually needed. */
export async function findOrCreateActionByKey(
  sourceKey: string,
  buildAction: () => Omit<Action, 'id' | 'ref' | 'createdAt' | 'updatedAt' | 'activity' | 'completedAt' | 'tenantId'>,
): Promise<{ actionId: string; created: boolean }> {
  const lockRef = doc(collections.actionLocks(), sourceKey);
  return runTransaction(db, async (tx) => {
    const lockSnap = await tx.get(lockRef);
    if (lockSnap.exists()) {
      return { actionId: (lockSnap.data() as ActionLock).actionId, created: false };
    }
    const actionRef = doc(collections.actions());
    const data = buildAction();
    tx.set(actionRef, {
      ...data,
      sourceKey,
      tenantId: resolveActiveTenantId(),
      completedAt: null,
      activity: [
        { by: data.createdBy, at: new Date().toISOString(), kind: 'event', text: 'created the Action.' },
      ],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as never);
    tx.set(lockRef, { actionId: actionRef.id, tenantId: resolveActiveTenantId(), createdAt: serverTimestamp() } as never);
    return { actionId: actionRef.id, created: true };
  });
}

/** Call when the Action a `sourceKey` lock points at resolves, so the key
 *  is free for a fresh Action if the same condition recurs later. Safe to
 *  call even if no lock exists for the key. */
export async function releaseActionLock(sourceKey: string): Promise<void> {
  await deleteDoc(doc(collections.actionLocks(), sourceKey));
}

export async function createGoal(data: New<Goal>): Promise<string> {
  const [ref] = await allocateRefs('goal');
  const created = await addDoc(collections.goals(), {
    ...data,
    ref,
    tenantId: resolveActiveTenantId(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as never);
  return created.id;
}

export async function createTask(data: New<Task>): Promise<string> {
  const [ref] = await allocateRefs('task');
  const created = await addDoc(collections.tasks(), {
    ...data,
    ref,
    tenantId: resolveActiveTenantId(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as never);
  return created.id;
}

/** Who a new roster Contact IS. A Contact is only ever created for a Person: an explicit existing Person, a
 *  my.Scouting Member ID (resolved server-side through the external-identity model), or a genuinely new human.
 *  Nothing here is ever matched by name, email or phone — those only produce advisory duplicate candidates. */
export type PersonChoice =
  | { personId: string }
  | { memberId: string }
  | { create: true; distinctFromPersonIds?: string[] };

export interface PersonCandidate {
  personId: string;
  displayName: string;
  status: 'active' | 'provisional';
  reasons: Array<'name' | 'similar_name' | 'email' | 'phone'>;
  districtIds: string[];
}

/** The server found possible duplicates and needs a human to choose one or confirm "different person". */
export class PersonConfirmationRequired extends Error {
  constructor(public candidates: PersonCandidate[], public hiddenCandidateCount: number) {
    super('This may be someone who is already in Compass.');
  }
}
/** The chosen Person already has a roster record. */
export class PersonAlreadyHasContact extends Error {
  constructor(public contactId: string) {
    super('This person already has a roster record.');
  }
}

export async function createContact(
  data: New<Contact>,
  choice: PersonChoice,
  requestId: string,
): Promise<string> {
  try {
    const result = await httpsCallable<
      { contact: Partial<Contact>; person: Record<string, unknown> },
      { id: string; created: boolean }
    >(functions, 'saveTenantContact')({ contact: contactMutationFields(data), person: { ...choice, requestId } });
    window.dispatchEvent(new Event('compass:contacts-changed'));
    return result.data.id;
  } catch (e) {
    const err = e as { code?: string; details?: { code?: string; candidates?: PersonCandidate[]; hiddenCandidateCount?: number; contactId?: string }; message?: string };
    if (err.details?.code === 'PERSON_CONFIRMATION_REQUIRED') {
      throw new PersonConfirmationRequired(err.details.candidates ?? [], err.details.hiddenCandidateCount ?? 0);
    }
    if (err.details?.code === 'PERSON_HAS_CONTACT' && err.details.contactId) throw new PersonAlreadyHasContact(err.details.contactId);
    throw e;
  }
}

const CONTACT_MUTATION_FIELDS = new Set<keyof Contact>([
  'districtId', 'positions', 'name', 'email', 'phone', 'notes',
]);

function contactMutationFields(data: Partial<Contact>): Partial<Contact> {
  return Object.fromEntries(
    Object.entries(data)
      .filter(([key, value]) =>
        CONTACT_MUTATION_FIELDS.has(key as keyof Contact)
        && (value !== undefined || key === 'phone' || key === 'notes'),
      )
      .map(([key, value]) => [key, value === undefined ? '' : value]),
  ) as Partial<Contact>;
}

/** Contact scope and Position transitions are validated by the server. `moveDistrict` must be sent only when someone deliberately
 *  changes the roster record's district: for a Person with district affiliations the server refuses any other district change. */
export async function updateContact(id: string, data: Partial<Contact>, options: { moveDistrict?: boolean } = {}): Promise<void> {
  await httpsCallable(functions, 'saveTenantContact')({
    id,
    contact: contactMutationFields(data),
    ...(options.moveDistrict ? { moveDistrict: true } : {}),
  });
  window.dispatchEvent(new Event('compass:contacts-changed'));
}

/** Administrator-only (enforced by the server, and only in a council that has enabled District Affiliations): add, change or remove
 *  a MANUAL district affiliation. Org Chart affiliations are managed through their assignments. `requestId` makes a retry safe;
 *  pass the same one for the same user action. */
export async function mutateDistrictAffiliation(
  personId: string,
  request:
    | { operation: 'add'; districtIds: string[]; description?: string }
    | { operation: 'update'; affiliationId: string; description?: string; districtId?: string }
    | { operation: 'remove'; affiliationId: string },
  requestId: string = newAffiliationRequestId(),
): Promise<void> {
  await httpsCallable(functions, 'mutateDistrictAffiliationV1')({ tenantId: resolveActiveTenantId(), personId, requestId, ...request });
  window.dispatchEvent(new Event('compass:contacts-changed'));
}
export const newAffiliationRequestId = (): string => `aff-${crypto.randomUUID()}`;

/** Deletes a roster Contact through the server. A Key 3 leader or Chair may delete one in their own district, but never one that
 *  holds a council-level Position; only an administrator may. The person stays in Contacts. */
export async function deleteContact(id: string): Promise<void> {
  await httpsCallable(functions, 'deleteTenantContact')({ id });
  window.dispatchEvent(new Event('compass:contacts-changed'));
}

/** Admin-only: set the manually maintained contact information on a Person.
 *  Imports never overwrite these; pass null (or empty) to clear one and fall
 *  back to the imported value. Enforced server-side by updatePersonContactInfoV1. */
export async function updatePersonContactInfo(params: {
  personId: string;
  preferredEmail?: string | null;
  secondaryEmail?: string | null;
  phone?: string | null;
  notes?: string | null;
  /** Record another address this same person is known by (never moves an address between people). */
  addAlternateEmail?: string;
  removeAlternateEmail?: string;
}): Promise<void> {
  await httpsCallable(functions, 'updatePersonContactInfoV1')({ tenantId: resolveActiveTenantId(), ...params });
  window.dispatchEvent(new Event('compass:contacts-changed'));
}

export interface ImportMatch {
  caseId: string;
  memberId: string;
  importedName: string;
  districtIds: string[];
  unitName: string | null;
  candidates: Array<{ personId: string; displayName: string; status: string; reasons: Array<'name' | 'similar_name' | 'email' | 'phone'>; districtIds: string[] }>;
}

/** Admin-only: imports held because a person added manually might be the same human (listImportMatchesV1). */
export async function listImportMatches(): Promise<ImportMatch[]> {
  const r = await httpsCallable<{ tenantId: string }, { matches: ImportMatch[] }>(functions, 'listImportMatchesV1')({ tenantId: resolveActiveTenantId() });
  return r.data.matches;
}

/** Admin-only decision: the imported record IS this existing Person, or is a different person (resolveImportMatchV1). */
export async function resolveImportMatch(params: { caseId: string; outcome: 'same_person' | 'different_person'; personId?: string }): Promise<void> {
  await httpsCallable(functions, 'resolveImportMatchV1')({ tenantId: resolveActiveTenantId(), ...params });
  window.dispatchEvent(new Event('compass:contacts-changed'));
}

export interface OrgChartPersonMatch {
  personId: string;
  displayName: string;
  status: 'active' | 'provisional';
  evidence: string[];
}

/** Read-only search over canonical Person identity (searchOrgChartPeopleV1,
 *  Phase 1) — finds an eligible Person for Org Chart assignment regardless
 *  of whether they already have a Contact record. Candidate discovery must
 *  not depend on an existing Contact; a Contact-only search can never
 *  surface someone who has never held a seat before. */
export async function searchOrgChartPeople(query: string): Promise<OrgChartPersonMatch[]> {
  const result = await httpsCallable<{ tenantId: string; query: string }, { people: OrgChartPersonMatch[] }>(
    functions,
    'searchOrgChartPeopleV1',
  )({ tenantId: resolveActiveTenantId(), query });
  return result.data.people;
}

/** Assign an already-resolved canonical Person directly to a Position seat
 *  (mutatePositionAssignmentV1, Phase 2A) — the governed path for
 *  assigning someone who has no Contact yet; the Function creates a
 *  compatibility Contact for them as part of the same transaction. */
export async function assignCanonicalPerson(params: {
  positionId: string;
  districtId: string | null;
  personId: string;
  reason: string;
}): Promise<{ affiliated: boolean }> {
  const result = await httpsCallable<object, { affiliated?: boolean }>(functions, 'mutatePositionAssignmentV1')({
    tenantId: resolveActiveTenantId(),
    operation: 'assign',
    positionId: params.positionId,
    districtId: params.districtId,
    personId: params.personId,
    requestId: `orgchart_${params.positionId}_${params.personId}_${Date.now()}`,
    reason: params.reason,
  });
  window.dispatchEvent(new Event('compass:contacts-changed'));
  return { affiliated: result.data?.affiliated === true };
}

/** End one person's hold on a seat (mutatePositionAssignmentV1, administrator-only on the server). Used for people who serve a
 *  district from outside it; they have no roster record to edit, so the seat is ended where it is held. */
export async function revokePositionAssignment(params: {
  positionId: string;
  districtId: string | null;
  personId: string;
  reason: string;
}): Promise<void> {
  await httpsCallable(functions, 'mutatePositionAssignmentV1')({
    tenantId: resolveActiveTenantId(),
    operation: 'revoke',
    positionId: params.positionId,
    districtId: params.districtId,
    personId: params.personId,
    requestId: `orgchart_revoke_${params.positionId}_${params.personId}_${Date.now()}`,
    reason: params.reason,
  });
  window.dispatchEvent(new Event('compass:contacts-changed'));
}

/** Grant (or refresh) a Compass login for a Contact. The client supplies
 *  only the target Contact and requested sign-in identity; the Function
 *  loads the Contact + tenant Position settings and derives the complete
 *  V1 authorization snapshot server-side. */
export async function grantLogin(params: {
  email: string;
  /** Legacy roster Contact the login is granted through. */
  contactId: string;
  /** The Person being granted a login (canonical). When both are sent the server requires them to agree. */
  personId?: string | null;
}): Promise<void> {
  await httpsCallable(functions, 'grantTenantLogin')({
    email: params.email.trim().toLowerCase(),
    contactId: params.contactId,
    ...(params.personId ? { personId: params.personId } : {}),
  });
}

/** Remove someone's Compass login (Key 3 / admin only — enforced by the
 *  removeTenantLogin Cloud Function; a chair has no delete permission at
 *  all). Leaves the Contact roster entry itself untouched — this only
 *  removes the ability to sign in. */
export async function removeLogin(email: string): Promise<void> {
  await httpsCallable(functions, 'removeTenantLogin')({ email: email.trim().toLowerCase() });
}

/** Council Access Phase A — admin-only update of one membership's
 *  Council/Contact Access. Omit a field to leave it unchanged; pass
 *  `null` to explicitly clear it back to unset. Enforced server-side by
 *  updateMembershipAccessV1 (admin-only, tenant-scoped) — see
 *  firestore.rules' tenantMemberships block, which stays write:false for
 *  every direct client write. */
export async function updateMembershipAccess(params: {
  email: string;
  councilAccess?: 'dashboardOnly' | 'fullRead' | 'fullRW' | null;
  contactAccess?: 'none' | 'read' | null;
  reason?: string | null;
}): Promise<void> {
  await httpsCallable(functions, 'updateMembershipAccessV1')({
    email: params.email.trim().toLowerCase(),
    ...(params.councilAccess !== undefined ? { councilAccess: params.councilAccess } : {}),
    ...(params.contactAccess !== undefined ? { contactAccess: params.contactAccess } : {}),
    ...(params.reason ? { reason: params.reason } : {}),
  });
}

export interface EffectiveAccessGrantSummary {
  capabilityKey: string;
  scope: string;
  contributorKinds: string[];
}

export interface EffectiveAccessResult {
  targetEmail: string;
  targetUid: string | null;
  claimed: boolean;
  role: string;
  isAdmin: boolean;
  councilAccess: 'dashboardOnly' | 'fullRead' | 'fullRW' | null;
  contactAccess: 'none' | 'read' | null;
  positions: string[];
  positionPolicyGrants: EffectiveAccessGrantSummary[] | null;
  positionPolicyUnavailableReason: string | null;
}

/** Read-only "why does this person have access" explanation — see
 *  getEffectiveAccessV1's doc comment. Never stored; always computed fresh. */
export async function getEffectiveAccess(email: string): Promise<EffectiveAccessResult> {
  const call = httpsCallable<{ email: string }, EffectiveAccessResult>(functions, 'getEffectiveAccessV1');
  const result = await call({ email: email.trim().toLowerCase() });
  return result.data;
}

export interface CommitteeReferenceEntry {
  type: 'position' | 'goal' | 'task' | 'initiative' | 'action' | 'childCommittee';
  id: string;
  name: string;
  relationship: string;
  scope?: string;
  notes?: string;
}

export interface CommitteeReferenceReport {
  committeeId: string;
  committeeName: string;
  organizationLevel: 'council' | 'district';
  parentId: string | null;
  references: CommitteeReferenceEntry[];
  totalReferences: number;
  byType: Record<'positions' | 'goals' | 'tasks' | 'initiatives' | 'actions' | 'childCommittees', number>;
  eligibleToDelete: boolean;
}

/** Committee Reference Inspection & Safe Delete — the canonical, server-
 *  authoritative dependency check. The Settings UI's reference count and
 *  the reference inspector both call this; never a separately maintained
 *  client-side approximation. Always computed fresh (never cached across
 *  calls) so it can't go stale between a read and a delete attempt. */
export async function inspectCommitteeReferences(committeeId: string): Promise<CommitteeReferenceReport> {
  const call = httpsCallable<{ committeeId: string }, CommitteeReferenceReport>(functions, 'inspectCommitteeReferencesV1');
  const result = await call({ committeeId });
  return result.data;
}

export type DeleteCommitteeResult =
  | { deleted: true; committeeId: string; committeeName: string }
  | { deleted: false; report: CommitteeReferenceReport };

/** Governed delete — the server re-verifies dependencies immediately
 *  before deleting (never trusts a reference count fetched earlier by
 *  this client). requestId makes the call safely replayable. */
export async function deleteCommittee(params: {
  committeeId: string;
  reason: string;
  requestId: string;
  expectedOrganizationLevel?: 'council' | 'district';
}): Promise<DeleteCommitteeResult> {
  const call = httpsCallable<typeof params, DeleteCommitteeResult>(functions, 'deleteCommitteeV1');
  const result = await call(params);
  return result.data;
}

export async function createEventTemplate(
  data: New<EventTemplate>,
): Promise<string> {
  const ref = await addDoc(collections.eventTemplates(), {
    ...data,
    tenantId: resolveActiveTenantId(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as never);
  return ref.id;
}

/** Create an initiative and its generated tasks in one atomic batch. Each task
 *  gets `initiativeId` set to the new doc and `source: 'initiative'`; callers
 *  build the rest of each task (title, dueDate, committee, status, …). */
export async function createInitiativeWithTasks(
  initiative: New<Initiative>,
  tasks: Array<Omit<New<Task>, 'initiativeId' | 'source'>>,
): Promise<string> {
  const result = await createInitiativeBatch([{ initiative: { ...initiative, specializedContactAreaIds: [] }, tasks }]);
  return result.initiatives[0].id;
}

export async function createInitiativeBatch(items: Array<{ initiative: object; tasks: object[] }>) {
  const result = await httpsCallable<object, { initiatives: Array<{id:string;ref:string}> }>(functions, 'createInitiatives')({tenantId:resolveActiveTenantId(),items});
  return result.data;
}

export async function updateTask(
  id: string,
  patch: Partial<Task>,
  updatedBy: string,
): Promise<void> {
  await updateDoc(doc(db, 'tasks', id), {
    ...patch,
    updatedAt: serverTimestamp(),
    updatedBy,
  } as never);
}

/** Append one note to a task's running log. */
export async function addTaskNote(
  id: string,
  entry: NoteEntry,
  updatedBy: string,
): Promise<void> {
  await updateDoc(doc(db, 'tasks', id), {
    noteLog: arrayUnion(entry),
    updatedAt: serverTimestamp(),
    updatedBy,
  } as never);
}

/** Update an initiative's key-fact fields (date, location, registration, chair)
 *  and append a Changes-log entry. When the event date is set/changed, pass
 *  `taskDueDates` to stamp due dates onto the offset-carrying tasks in one
 *  atomic batch. */
export async function updateInitiativeFacts(
  id: string,
  patch: Partial<Initiative>,
  change: ChangeEntry,
  taskDueDates: Array<{ id: string; dueDate: string }> = [],
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, 'initiatives', id), {
    ...patch,
    changeLog: arrayUnion(change),
    updatedAt: serverTimestamp(),
  } as never);
  for (const t of taskDueDates) {
    batch.update(doc(db, 'tasks', t.id), {
      dueDate: t.dueDate,
      updatedAt: serverTimestamp(),
    } as never);
  }
  await batch.commit();
}

/** Append a Changes-log entry without touching other fields. */
export async function addInitiativeChange(
  id: string,
  change: ChangeEntry,
): Promise<void> {
  await updateDoc(doc(db, 'initiatives', id), {
    changeLog: arrayUnion(change),
    updatedAt: serverTimestamp(),
  } as never);
}

/** Append one human note to an Action's activity timeline — the narrowest
 *  possible write (only `activity`/`updatedAt`), matching what a
 *  participant (not the owner) is allowed under firestore.rules. Never
 *  sets `updatedBy`, so a participant's write can't touch it. */
export async function addActionNote(id: string, text: string, by: string): Promise<void> {
  const entry: ActionActivityEntry = { by, at: new Date().toISOString(), kind: 'note', text };
  await updateDoc(doc(db, 'actions', id), {
    activity: arrayUnion(entry),
    updatedAt: serverTimestamp(),
  } as never);
  window.dispatchEvent(new Event(ACTIONS_CHANGED_EVENT));
}

/** Change Action fields and append the matching system-event activity
 *  entries in one atomic write. Only call this for MEANINGFUL changes
 *  (status/priority/owner/due date/participants/completion/reopen) — see
 *  the Actions page for what counts; don't log every trivial field edit. */
export async function updateActionWithActivity(
  id: string,
  // Record<string, unknown> rather than Partial<Action> — callers sometimes
  // need to pass a Firestore sentinel (serverTimestamp() for completedAt)
  // which doesn't fit the Timestamp type Action declares for reads.
  patch: Record<string, unknown>,
  activityText: string[],
  by: string,
): Promise<void> {
  const now = new Date().toISOString();
  const entries: ActionActivityEntry[] = activityText.map((text) => ({
    by,
    at: now,
    kind: 'event',
    text,
  }));
  await updateDoc(doc(db, 'actions', id), {
    ...patch,
    ...(entries.length ? { activity: arrayUnion(...entries) } : {}),
    updatedAt: serverTimestamp(),
    updatedBy: by,
  } as never);
  window.dispatchEvent(new Event(ACTIONS_CHANGED_EVENT));
}

export async function updateDocFields<T>(
  path: string,
  id: string,
  patch: Partial<T>,
): Promise<void> {
  await updateDoc(doc(db, path, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  } as never);
}

export async function removeDoc(path: string, id: string): Promise<void> {
  await deleteDoc(doc(db, path, id));
  if (path === 'contacts') window.dispatchEvent(new Event('compass:contacts-changed'));
}

/** Delete an initiative and every task under it. Batched in chunks of 400
 *  (Firestore's per-batch write limit is 500). */
export async function deleteInitiativeCascade(id: string): Promise<number> {
  const taskSnap = await getDocs(
    query(collections.tasks(), where('initiativeId', '==', id), tenantWhere()),
  );
  const taskIds = taskSnap.docs.map((d) => d.id);
  for (let i = 0; i < taskIds.length; i += 400) {
    const batch = writeBatch(db);
    for (const tid of taskIds.slice(i, i + 400)) {
      batch.delete(doc(db, 'tasks', tid));
    }
    await batch.commit();
  }
  await deleteDoc(doc(db, 'initiatives', id));
  return taskIds.length;
}

export async function saveSettings(data: AppSettings): Promise<void> {
  await setDoc(
    settingsRef(),
    { ...data, tenantId: resolveActiveTenantId(), updatedAt: serverTimestamp() },
    { merge: true },
  );
}

// ---- Data Imports — Import Management --------------------------------
// Immediate-write pattern (no draft/save-bar) — matches Backup/the
// automation tabs in Settings.tsx, not the batched "App Settings" tab.

/** Patches the tenant-level defaults and/or one or more per-report
 *  overrides in a single merge write. `patch.overrides` merges per-report
 *  (Firestore deep-merges nested map fields under {merge:true} — the same
 *  guarantee importUnitReport()/etc. already rely on for
 *  charterOrgDetail-style partial unit patches in lib/units.ts), so
 *  patching one report's override never touches any other report's. */
export async function saveDataImportAdminSettings(
  patch: Partial<Omit<DataImportAdminSettings, 'tenantId' | 'updatedAt' | 'lastAttempt'>>,
): Promise<void> {
  await setDoc(
    dataImportAdminRef(),
    { ...patch, tenantId: resolveActiveTenantId(), updatedAt: serverTimestamp() },
    { merge: true },
  );
}

/** Fully clears ONE report's override (all three fields at once — the
 *  "simple Use defaults / Reset to defaults action"), reverting it to
 *  100% inherited. Uses updateDoc + deleteField() on the specific
 *  dot-path, not setDoc/merge, since merge can only ADD/replace fields,
 *  never remove one — see effectiveReportImportSettings() in
 *  lib/dataImports.ts for how an absent override is read back as "inherit
 *  everything." */
export async function resetDataImportReportOverride(reportType: UnitReportType): Promise<void> {
  await updateDoc(dataImportAdminRef(), {
    [`overrides.${reportType}`]: deleteField(),
    tenantId: resolveActiveTenantId(),
    updatedAt: serverTimestamp(),
  } as never);
}

/** Records the outcome of ONE import attempt (success or failure) for one
 *  report type — always overwrites the previous attempt for that same
 *  report type, never accumulates a log. Deliberately separate from (and
 *  never a substitute for) the unitImports audit collection: this is the
 *  ONLY place a FAILED attempt is ever recorded at all, since none of the
 *  importers in lib/units.ts write anything to Firestore on failure — see
 *  DataImportAttempt's doc comment in ../types. Called by the import UI
 *  itself right after each attempt; never changes unitImports, never
 *  resets a freshness clock, never touches unitLeaders/
 *  unitLeaderSnapshots. */
export async function recordDataImportAttempt(
  reportType: UnitReportType,
  attempt: Omit<DataImportAttempt, 'at'>,
): Promise<void> {
  await setDoc(
    dataImportAdminRef(),
    {
      tenantId: resolveActiveTenantId(),
      lastAttempt: { [reportType]: { ...attempt, at: serverTimestamp() } },
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

// ---- Qualifications --------------------------------------------------
// Council-maintained specialized roles/certifications (NCAP, Shooting
// Sports, ...) — see the QualificationArea/QualificationAssignment doc
// comments in ../types and lib/qualifications.ts for the data model.
// Both collections are admin-only writes for this increment (enforced by
// firestore.rules, mirrored client-side by canManageQualifications() in
// lib/visibility.ts). Never writes to `contacts` — assignments reference
// an existing Contact only by id.

export async function createQualificationArea(data: {
  name: string;
  description?: string;
}): Promise<string> {
  const created = await addDoc(collections.qualificationAreas(), {
    tenantId: resolveActiveTenantId(),
    name: data.name,
    description: data.description ?? '',
    roleTypes: [],
    qualificationTypes: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as never);
  return created.id;
}

/** Renaming/removing a role or qualification type, and editing an area's
 *  own name/description, all go through this one function — the caller
 *  (QualificationsSettings.tsx) computes the full new roleTypes/
 *  qualificationTypes array from the area it already has loaded and passes
 *  it here, the same "whole-array replace" pattern Settings.tsx's own
 *  PositionsCard/RecordListCard already use for positions/districts/
 *  committees, just written immediately instead of staged in a draft. */
export async function updateQualificationArea(
  id: string,
  patch: Partial<Pick<QualificationArea, 'name' | 'description' | 'roleTypes' | 'qualificationTypes'>>,
): Promise<void> {
  await updateDoc(doc(collections.qualificationAreas(), id), {
    ...patch,
    updatedAt: serverTimestamp(),
  } as never);
}

/** Grants one area role or qualification to one person — the atomic
 *  "checkbox" (see QualificationAssignment's doc comment in ../types).
 *  Holding both Short-Term and Long-Term Camp Assessor is two calls to
 *  this, never a combined field.
 *
 *  The server doc id is deterministic — tenantId__areaId__personId__kind__typeId,
 *  the same composite-key-as-id pattern firestore.rules already uses for
 *  tenantMemberships — so this grant can never duplicate: a second call
 *  with the same (area, person, kind, type) returns the existing grant
 *  without resetting its creation time or expiration, rather than producing
 *  a second row. The UI's heldTypeIds() already
 *  keeps this from happening in the normal add flow; this is the
 *  data-level guarantee for a race (two admins granting at once) or any
 *  future caller that bypasses that check. */
export async function grantQualification(data: {
  areaId: string;
  personId: string;
  kind: 'role' | 'qualification';
  typeId: string;
  expiresOn?: ISODateString | null;
}): Promise<string> {
  const tenantId = resolveActiveTenantId();
  const result = await httpsCallable<object, { id: string }>(functions, 'manageQualificationAssignment')({
    tenantId, operation: 'grant', areaId: data.areaId, personId: data.personId,
    kind: data.kind, typeId: data.typeId, expiresOn: data.expiresOn ?? null,
  });
  return result.data.id;
}

export async function updateQualificationExpiration(
  id: string,
  expiresOn: ISODateString | null,
): Promise<void> {
  await httpsCallable(functions, 'manageQualificationAssignment')({ tenantId: resolveActiveTenantId(), operation: 'expiration', id, expiresOn });
}

/** Revokes one role/qualification grant. Deletes only the assignment doc —
 *  never touches the underlying Person, and never affects any of that
 *  person's OTHER assignments (in this area or any other). */
export async function revokeQualification(id: string): Promise<void> {
  await httpsCallable(functions, 'manageQualificationAssignment')({ tenantId: resolveActiveTenantId(), operation: 'revoke', id });
}
